import { gql } from 'apollo-boost';
import { useMutation } from '@apollo/react-hooks';
import { useLogger } from '../contexts/Logger';

export const SEND_OTP = gql`
    mutation SendLoggedInOtp($request: SendOtpRequestType!) {
        sendLoggedInOtp(request: $request) {
            success
            verificationToken
        }
    }
`;

export const CREATE_WITHDRAWAL = gql`
    mutation CreateWithdrawal($request: CreateWithdrawalType!) {
        createWithdrawal(request: $request) {
            reference
            amount
            updatedBalance
            availableBalance
            bookingDate
            withdrawalDate
            counterpartAccountNumber
            counterpartSortCode
            updatedInterestRate
            action
        }
    }
`;

export const getWithdrawalError = (error: unknown) => ({
    type: error instanceof Error && error.message.includes('InvalidOtpError') ? 'otp' : 'unexpected',
    message: error instanceof Error && error.message.includes('InvalidOtpError')
        ? 'Your code has either expired or been entered incorrectly. Please try again or click below to resend a new code.'
        : 'An unexpected error occurred, please try again later.'
});

// Both views send and verify through the same API. Verification remains atomic with payment.
export const useWithdrawalVerification = (journey = 'Withdrawal') => {
    const logger = useLogger();
    const report = (stage: string, details: Record<string, unknown>, failed = false) => {
        // Logging must not change the payment result. Do not include request bodies or error messages.
        try {
            const context = { additionalData: { journey, stage, ...details } };
            // /logs forwards only the message to diagnostic logs. Keep the safe result there too.
            const message = `[WithdrawalDiagnostic] Client ${journey} ${stage} ${failed ? 'failed' : 'result'}; codes: ${details.errorCodes ?? ''}; success: ${details.success ?? ''}`;
            if (failed) logger?.warn(message, context);
            else logger?.info(message, context);
        } catch { /* Keep the original operation result. */ }
    };
    const errorDetails = (error: unknown) => {
        const graphqlErrors = (error as { graphQLErrors?: Array<{ extensions?: { code?: string } }> } | null)?.graphQLErrors;
        return {
            errorCodes: graphqlErrors?.map(e => e.extensions?.code)
                .filter(code => typeof code === 'string' && /^[A-Z_0-9]+$/.test(code)).join(',') ?? '',
            errorType: getWithdrawalError(error).type
        };
    };
    const [sendOtp] = useMutation(SEND_OTP);
    const [createWithdrawal] = useMutation(CREATE_WITHDRAWAL);

    const sendStepUpOtp = async () => {
        try {
            const result = await sendOtp({ variables: { request: {} } });
            const token = result?.data?.sendLoggedInOtp?.verificationToken;
            report('OtpSend', { success: result?.data?.sendLoggedInOtp?.success ?? false, tokenPresent: !!token },
                result?.data?.sendLoggedInOtp?.success === false || !token);
            if (!token) throw new Error('Missing OTP verification token');
            return token as string;
        } catch (error) {
            report('OtpSend', errorDetails(error), true);
            throw error;
        }
    };

    const submitWithdrawal = async (request: Record<string, unknown>, clientAvailableBalance?: number) => {
        const details = {
            sourceAccountId: request.id, amount: request.amount, otpSupplied: !!request.verifyOtp,
            clientTotalBalance: request.previousBalance, clientAvailableBalance
        };
        report('PaymentSubmit', details);
        try {
            const result = await createWithdrawal({ variables: { request } });
            const withdrawal = result?.data?.createWithdrawal;
            if (!withdrawal) throw new Error('Missing withdrawal result');
            report('PaymentAccepted', details);
            return withdrawal;
        } catch (error) {
            report('PaymentSubmit', { ...details, ...errorDetails(error) }, true);
            throw error;
        }
    };

    return { sendStepUpOtp, submitWithdrawal };
};

// Display updates must never turn an accepted payment into a retryable payment error.
export const updateAfterWithdrawal = (update: () => void, report: () => void) => {
    try {
        update();
    } catch {
        try { report(); } catch { /* Logging must not change the payment outcome. */ }
    }
};
